import { ChangeEvent, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';

import { zIndex } from 'styles';

import { sendContactForm } from 'client';
import { validateEmail } from 'components/ContactForm/utils';
import { useDateCheck } from 'hooks/useDateCheck';
import { TARGET_DATE } from 'constants/common';

import {
  Button,
  EmailInput,
  EmailInputContainer,
  FormControlContainer,
  ModalContent,
} from './ContactModal.style';

type ContactModalType = 'apply' | 'default';

type ContactModalProps = { onClose: () => void; isOpen: boolean; mode?: ContactModalType };

export const ContactModal = ({ isOpen, onClose, mode = 'default' }: ContactModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [isValid, setIsValid] = useState(true);

  const { isTargetDate } = useDateCheck(TARGET_DATE);
  const location = useLocation();

  const label = mode === 'apply' && !isTargetDate ? 'Apply now' : 'Contact us';
  const pageName = location.pathname === '/' ? 'homepage' : location.pathname.substring(1);
  const pageTag = `${pageName}-${label.split(' ').join('-').toLowerCase()}`;

  const handleClose = useCallback(() => {
    setEmail('');
    setName('');
    setMessage('');
    setIsValid(true);
    onClose();
  }, [onClose]);

  const onSubmit = useCallback(() => {
    if (!validateEmail(email)) {
      setIsValid(false);
      return;
    }

    setIsValid(true);
    setIsLoading(true);

    sendContactForm(email, pageTag, name.trim() || undefined, message.trim() || undefined)
      .then(() => {
        setIsLoading(false);
        handleClose();
      })
      .catch(() => {
        setIsLoading(false);
        setIsValid(false);
      });
  }, [email, name, message, pageTag, handleClose]);

  const onEmailChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(e.currentTarget.value);
  }, []);

  const onNameChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setName(e.currentTarget.value);
  }, []);

  const onMessageChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setMessage(e.currentTarget.value);
  }, []);

  return (
    <Modal
      sx={{
        zIndex: zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="contact-modal-title"
      aria-describedby="contact-modal-description"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0,0,0,0.2)',
          },
        },
      }}
    >
      <ModalContent
        sx={{ maxWidth: { xxs: 'unset', sm: 480 }, px: { xxs: 3, sm: 4 }, py: { xxs: 3, sm: 4 } }}
      >
        <Typography id="contact-modal-title" variant="h6" component="h2">
          {label}
        </Typography>
        <Typography id="contact-modal-description" sx={{ mt: 2 }}>
          Discover the benefits of synthetic data and simulation
        </Typography>
        <FormControlContainer
          sx={{
            mt: 3,
            gap: 1.5,
            width: '100%',
            flexDirection: 'column',
            alignItems: 'stretch',
            '@media (min-width: 600px)': {
              flexDirection: 'column',
              alignItems: 'stretch',
              flexBasis: 'auto',
            },
          }}
        >
          <EmailInputContainer>
            <EmailInput
              disabled={isLoading}
              fullWidth
              value={name}
              placeholder="Your name"
              onChange={onNameChange}
              aria-label="Your name"
            />
          </EmailInputContainer>
          <EmailInputContainer>
            <EmailInput
              error={!isValid}
              disabled={isLoading}
              fullWidth
              value={email}
              placeholder="Email address"
              onChange={onEmailChange}
              aria-describedby="email-error"
              aria-invalid={!isValid}
            />
            {!isValid && (
              <span id="email-error" className="sr-only">
                Please enter a valid email address.
              </span>
            )}
          </EmailInputContainer>
          <EmailInputContainer>
            <EmailInput
              disabled={isLoading}
              fullWidth
              multiline
              minRows={3}
              value={message}
              placeholder="Your message"
              onChange={onMessageChange}
              aria-label="Your message"
            />
          </EmailInputContainer>
          <Button
            disabled={isLoading}
            size="large"
            onClick={onSubmit}
            aria-label="Take the first step and subscribe"
            sx={{ width: '100% !important', mt: 1 }}
          >
            Take the first step
          </Button>
        </FormControlContainer>
      </ModalContent>
    </Modal>
  );
};
